import axios from "axios";
import Secure from "../secure/payload";
import { API, C_API } from "../utils/helper/api";
import { API_URLS } from "@/utils/constant/api.constant";
import { useQuery } from "@tanstack/react-query";

export const register = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["recaptcha"] });
  try {
    const response = await API.post(API_URLS.AUTH.SIGNUP, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const login = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["recaptcha"] });
  try {
    const response = await C_API.post(API_URLS.AUTH.SIGNIN, encryptData);

    return { status: response.status, data: response.data };
  } catch (err: any) {
    if (err.response) {
      return { status: err.response.status, data: err?.response?.data };
    } else if (err.request) {
      return { status: err.response.status, message: "No response received from server" };
    } else {
      return { status: err.response.status, message: err.message };
    }
  }
};

export const googleLoginOrRegister = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.AUTH.GOOGLE_AUTH, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const validateMFA = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["token"] });
  try {
    const response = await C_API.post(API_URLS.AUTH.MFA_VALIDATE, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const verifyEmail = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.AUTH.VERIFY_EMAIL, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const sendResetPasswordLink = async (data: any, param: string = "") => {
  let apiUrl = API_URLS.AUTH.RESET_PASSWORD_SEND_LINK;
  if (param) {
    apiUrl += param;
  }

  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await API.post(apiUrl, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const validateResetPasswordLink = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["token"] });
  try {
    const response = await API.post(API_URLS.AUTH.RESET_PASSWORD_VALIDATE_LINK, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const getCountryList = async () => {
  try {
    const response = await axios.get(API_URLS.EXTERNAL.COUNTRY_LIST);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const publicResendCode = async (data: any, param: string = "") => {
  let apiUrl = API_URLS.AUTH.PUBLIC_RESEND;
  if (param) {
    apiUrl += param;
  }
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: param ? ["token"] : undefined });
  try {
    const response = await API.post(apiUrl, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

const fetchCurrencyList = async () => {
  try {
    const response = await API.get(API_URLS.CONFIG.OPTIONS);
    return response.data?.data;
  } catch (err) {
    throw new Error("Failed to fetch currency list");
  }
};

export const useCurrencyList = () => {
  return useQuery({
    queryKey: ["currencyList"],
    queryFn: fetchCurrencyList,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });
};

// Fetch countries data from external URL with long cache
const fetchCountriesData = async () => {
  try {
    const response = await axios.get("https://www.lystloc.com/countries");
    return response.data;
  } catch (err) {
    throw new Error("Failed to fetch countries data");
  }
};

export const useCountriesData = () => {
  return useQuery({
    queryKey: ["countriesData"],
    queryFn: fetchCountriesData,
    staleTime: 30 * 24 * 60 * 60 * 1000, // 30 days
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });
};
